import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

import { StripeService, StripePaymentElementComponent } from 'ngx-stripe';
import {
  StripeElementsOptions,
  Appearance,
  PaymentIntentResult,
} from '@stripe/stripe-js';
import { PaymentService } from 'src/services/payment.service';
import {
  AlertController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-stripe-payment',
  templateUrl: './stripe-payment.component.html',
  styleUrls: ['./stripe-payment.component.scss'],
})
export class StripePaymentComponent implements OnInit {
  @ViewChild(StripePaymentElementComponent)
  paymentElement: StripePaymentElementComponent;
  @Input() request: any;
  @Input() name: string;
  @Input() stripeKeys: {
    paymentIntentClientSecret: string;
    customerEphemeralKeySecret: string;
    customerId: string;
  };
  @Input() isTestPayment = false;
  paymentMethods: any[];
  appearance: Appearance = {
    theme: 'stripe',
    labels: 'floating',
    variables: {
      colorPrimary: '#673ab7',
    },
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
  };

  selectedPaymentMethodId: string;
  payForm: FormGroup;
  editPayMethods = false;
  addPayMethod = true;
  displayRequest: any;
  isLoading = false;
  showFees = false;
  private isPaying = false;

  constructor(
    private fb: FormBuilder,
    private stripeService: StripeService,
    private paymentService: PaymentService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
  ) {}

  //by the time this is invoked, the payment intent has already been created.
  //here we are going to show existing payment methods
  //or let them input a new card

  ngOnInit(): void {
    this.stripeService.changeKey(environment.stripe.forceTestPayment || this.isTestPayment ? environment.stripe.testPublishableKey : environment.stripe.publishableKey);
    
    if (!this.stripeKeys) {
      this.processRequest();
    } else if (this.stripeKeys) {
      this.getCustomerPayMethods();
      this.displayRequest = this.request;
      this.initPayForm();
    }
  }

  private processRequest() {
    this.isLoading = true;
    this.paymentService
      .getPaymentDisplayInfo(this.request)
      .then(async (displayRequest: any) => {
        console.log(displayRequest);
        this.displayRequest = displayRequest;

        this.initPayForm();

        if (this.displayRequest && this.displayRequest.total > 0) {
          const stripeKeys = await this.paymentService.createPaymentIntent(
            this.request,
          );
          console.log({ stripeKeys });
          this.stripeKeys = stripeKeys as any;
          this.getCustomerPayMethods();
        }
      })
      .catch((err) => {
        console.error(err);
        this.toastCtrl
          .create({
            message: err.error || err.message,
            color: 'danger',
            duration: 2500,
          })
          .then((x) => x.present());
        this.modalCtrl.dismiss();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private initPayForm() {
    this.payForm = this.fb.group({
      name: [this.name, [Validators.required]],
      amount: [this.displayRequest.total / 100, [Validators.required]],
    });
  }

  completeOrder() {
    if (this.displayRequest && this.displayRequest.total > 0) {
      this.collectPayment();
    } else {
      //not sure how to do this...
      //need to just call the update membership endpoint?
      this.paymentService
        .createAndProcessOrder(this.request, true)
        .then((order) => {
          console.log(order);
          if (order) {
            this.close(order);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  collectPayment(): void {
    if (this.isPaying) return;
    if (this.payForm.invalid) {
      return;
    }

    this.isPaying = true;
    this.isLoading = true;
    if (this.selectedPaymentMethodId && !this.addPayMethod) {
      //existing payment method
      this.stripeService
        .confirmCardPayment(this.stripeKeys.paymentIntentClientSecret, {
          payment_method: this.selectedPaymentMethodId,
        })
        .subscribe({
          next: (result) => this.handleStripeResponse(result),
          error: (error) => {
            this.handleError(error);
          },
        });
    } else {
      //new payment method
      this.stripeService
        .confirmPayment({
          elements: this.paymentElement.elements,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: this.payForm.get('name')?.value,
              },
            },
          },
          redirect: 'if_required',
        })
        .subscribe({
          next: (result) => this.handleStripeResponse(result),
          error: (error) => {
            this.handleError(error);
          },
        });
    }
  }

  close(data: any) {
    this.modalCtrl.dismiss(data);
  }

  private async getCustomerPayMethods() {
    this.addPayMethod = !this.paymentMethods || this.paymentMethods.length == 0;
    this.editPayMethods = false;
  }

  private async handleStripeResponse(result: PaymentIntentResult) {
    this.isPaying = true;
    this.isLoading = true;

    if (result.error) {
      // Show error to your customer (e.g., insufficient funds)
      console.log(result.error.message);
      this.alertCtrl
        .create({
          header: 'Stripe Error',
          subHeader: result.error.code,
          message: result.error.message,
        })
        .then((x) => x.present());
    } else {
      // The payment has been processed!
      if (result.paymentIntent.status === 'succeeded') {
        // Show a success message to your customer
        this.toastCtrl
          .create({
            message: 'Payment completed!',
            color: 'success',
            buttons: ['X'],
          })
          .then((x) => x.present());
        this.close(result);
      }
    }
  }

  private handleError(error: any) {
    this.alertCtrl
    .create({
      header: 'Stripe Error',
      message: JSON.stringify(error),
    })
    .then((x) => x.present());
    this.isLoading = false;
  }
}
